// src/features/task/useResolvedTaskConfig.ts
// What the scope a task type is configured in offers on its own: the statuses,
// template and pipeline a type with none of its own would follow.
//
// The call deliberately omits `task_type_id`. Resolution is type-first, so
// passing the type would hand back the type's own values and the screen would
// learn nothing about the scope — which is the one thing it needs to show. Ask
// about the scope alone and the answer means the same in both directions: what
// applies when this type stays silent, and what it replaces when it does not.
//
// The resolver returns references, not names. `available_templates` on the same
// response usually names the template; a pipeline needs one more read. Neither
// lookup is allowed to fail the screen — a missing name degrades the sentence,
// it does not turn inheritance into an error.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n, useTaskTaskPolicy, useTaskTemplate, useWorkflow } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { errMsg, makeEntity } from '@/features/task/panelHelpers';
import type { Entity } from '@core/types/entity';
import type { ResolvedReference, ResolvedStatusSet, TaskTemplate } from '@core/types/task';

export interface ResolvedTaskConfig {
  /** The status set the scope offers, with its origin. */
  statuses?: ResolvedStatusSet;
  /** The template the scope offers, with its origin. */
  template?: ResolvedReference;
  /** The pipeline the scope offers, with its origin. */
  pipeline?: ResolvedReference;
  /** Display name of the inherited template, or '' when it could not be read. */
  templateName: string;
  /** Display name of the inherited pipeline, or '' when it could not be read. */
  pipelineName: string;
}

export interface ResolvedTaskConfigState {
  /** The scope entity the question was asked about — origins compare against it. */
  scopeEntity: Entity;
  data: ResolvedTaskConfig | null;
  isLoading: boolean;
  /** Human-readable failure, or null. Distinct from "resolved, but nothing set". */
  error: string | null;
  reload: () => void;
}

type TemplateStore = ReturnType<typeof useTaskTemplate>;
type WorkflowStore = ReturnType<typeof useWorkflow>;

async function readTemplateName(
  id: string,
  fromResponse: TaskTemplate[] | undefined,
  store: TemplateStore,
): Promise<string> {
  if (!id) return '';
  const known = (fromResponse || []).find((tpl) => tpl.entity?.id === id);
  if (known?.name) return known.name;
  try {
    const fetched = await store.GetTemplate(id);
    return fetched?.name || '';
  } catch {
    return '';
  }
}

async function readPipelineName(id: string, store: WorkflowStore): Promise<string> {
  if (!id) return '';
  try {
    const res = await store.GetPipeline(id);
    return res?.pipeline?.name || '';
  } catch {
    return '';
  }
}

/**
 * Resolve what the current scope offers a task type.
 *
 * The type being edited is not passed here — see the file header. Callers that
 * need to recognise it inside an origin hand it to `inheritedStatusSet` /
 * `inheritedReference` in ./inheritance, which refuse to word a type's own
 * value as inherited from itself.
 */
export function useResolvedTaskConfig(): ResolvedTaskConfigState {
  const policyStore = useTaskTaskPolicy();
  const templateStore = useTaskTemplate();
  const workflowStore = useWorkflow();
  const scope = useScope();
  const { t } = useI18n();

  const scopeEntity = useMemo(
    () => makeEntity(scope.tenantId, 'tenant', scope),
    [scope],
  );

  const [data, setData] = useState<ResolvedTaskConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await policyStore.ResolveTaskContext({
          scope_entity: scopeEntity,
          scope_chain: [scopeEntity],
        });
        if (cancelled) return;

        const [templateName, pipelineName] = await Promise.all([
          readTemplateName(
            res.effective_template?.reference?.id || '',
            res.available_templates,
            templateStore,
          ),
          readPipelineName(res.effective_pipeline?.reference?.id || '', workflowStore),
        ]);
        if (cancelled) return;

        setData({
          statuses: res.effective_statuses,
          template: res.effective_template,
          pipeline: res.effective_pipeline,
          templateName,
          pipelineName,
        });
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setError(errMsg(e, t('task.inheritance.loadError')));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // `t` is stable per locale and would otherwise re-resolve on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEntity, attempt, policyStore, templateStore, workflowStore]);

  return { scopeEntity, data, isLoading, error, reload };
}
